import { useState, useEffect, useCallback } from "react"
import { supabase, logAuditAction } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Modal, FormGroup, Input, Textarea, Select } from "@/components/common/Modal"
import { Button } from "@/components/common/Button"
import { Badge } from "@/components/common/Badge"
import { EmptyState } from "@/components/common/EmptyState"
import { PageLoader } from "@/components/common/Spinner"
import type { Announcement } from "@/types"
import { Plus, Send, Mail, Bell } from "lucide-react"
import { useAuth } from "@/context/AuthContext"

const AUDIENCE_LABELS: Record<string, string> = {
  all: "All Users",
  active: "Active Only",
  inactive: "Inactive",
  grace_period: "Grace Period",
}

function AnnouncementRow({ a }: { a: Announcement }) {
  return (
    <tr className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <p className="font-bold text-sm text-gray-900">{a.title}</p>
        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{a.content}</p>
      </td>
      <td className="px-6 py-4">
        <Badge variant="info">{AUDIENCE_LABELS[a.audience] ?? a.audience}</Badge>
      </td>
      <td className="px-6 py-4">
        <div className="flex gap-1.5">
          {a.send_email && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
              <Mail size={11} /> Email
            </span>
          )}
          {a.send_in_app && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-zimrah-600 bg-zimrah-50 px-2 py-1 rounded-lg">
              <Bell size={11} /> In-App
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        {a.sent_at
          ? <Badge variant="success">Sent</Badge>
          : <Badge variant="muted">Draft</Badge>}
      </td>
      <td className="px-6 py-4 text-sm text-gray-400">
        {a.sent_at ? new Date(a.sent_at).toLocaleDateString() : "—"}
      </td>
    </tr>
  )
}

interface AnnouncementForm {
  title: string
  content: string
  audience: string
  send_email: boolean
  send_in_app: boolean
}

const EMPTY: AnnouncementForm = {
  title: "", content: "", audience: "all", send_email: true, send_in_app: true,
}

export function AnnouncementsPage() {
  const { toast } = useToast()
  const { user } = useAuth()
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<AnnouncementForm>(EMPTY)
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .order("created_at", { ascending: false })
      if (error) throw error
      setAnnouncements((data ?? []) as Announcement[])
    } catch {
      toast("Failed to load announcements", "error")
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  const handleSend = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast("Title and content are required", "error")
      return
    }
    setSending(true)
    try {
      // 1 — Save to database
      const { data: saved, error } = await supabase
        .from("announcements")
        .insert({
          title: form.title,
          content: form.content,
          audience: form.audience,
          send_email: form.send_email,
          send_in_app: form.send_in_app,
          sent_at: new Date().toISOString(),
          created_by: user?.id ?? null,
          created_at: new Date().toISOString(),
        })
        .select()
        .single()

      if (error) throw error

      // 2 — Trigger email via Supabase Edge Function (deploy send-announcement to Supabase)
      if (form.send_email) {
        const { error: fnError } = await supabase.functions.invoke("send-announcement", {
          body: {
            announcementId: saved.id,
            title: form.title,
            content: form.content,
            audience: form.audience,
          },
        })
        if (fnError) {
          // Edge function not deployed yet — log warning, do not block
          console.warn("send-announcement Edge Function not deployed:", fnError.message)
          toast("Saved to DB. Deploy send-announcement Edge Function to enable email delivery.", "warning")
        } else {
          toast(`Announcement sent to ${AUDIENCE_LABELS[form.audience]}!`, "success")
        }
      } else {
        toast("Announcement saved (in-app only)", "success")
      }

      await logAuditAction("announcement_sent", null, `Sent: "${form.title}" to ${form.audience}`)
      setOpen(false)
      setForm(EMPTY)
      await load()
    } catch (err) {
      console.error(err)
      toast("Failed to send announcement", "error")
    } finally {
      setSending(false)
    }
  }

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Button variant="primary" icon={<Plus size={15} />} onClick={() => setOpen(true)}>
          New Announcement
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">All Announcements</h2>
        </div>
        {announcements.length === 0 ? (
          <EmptyState
            icon="📢"
            title="No announcements yet"
            subtitle="Create your first announcement to notify your members."
            action={
              <Button variant="primary" icon={<Plus size={15} />} onClick={() => setOpen(true)}>
                New Announcement
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {["Title", "Audience", "Channels", "Status", "Sent"].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {announcements.map(a => <AnnouncementRow key={a.id} a={a} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Announcement Modal */}
      <Modal
        open={open}
        onClose={() => { setOpen(false); setForm(EMPTY) }}
        title="New Announcement"
        icon="📢"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setForm(EMPTY) }}>Cancel</Button>
            <Button variant="primary" icon={<Send size={14} />} loading={sending} onClick={handleSend}>
              Send Announcement
            </Button>
          </>
        }
      >
        <FormGroup label="Title" required>
          <Input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Important update for all members..."
          />
        </FormGroup>

        <FormGroup label="Content" required>
          <Textarea
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Write your announcement message here..."
            style={{ minHeight: 160 }}
          />
        </FormGroup>

        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Audience">
            <Select
              value={form.audience}
              onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive</option>
              <option value="grace_period">Grace Period</option>
            </Select>
          </FormGroup>

          <FormGroup label="Delivery Channels">
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-zimrah-600 focus:ring-zimrah-500"
                />
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Mail size={14} className="text-blue-500" /> Email
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_in_app}
                  onChange={e => setForm(f => ({ ...f, send_in_app: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-zimrah-600 focus:ring-zimrah-500"
                />
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Bell size={14} className="text-zimrah-500" /> In-App
                </span>
              </label>
            </div>
          </FormGroup>
        </div>

        {form.send_email && (
          <div className="mt-2 p-4 rounded-xl bg-blue-50 border border-blue-100">
            <p className="text-xs font-semibold text-blue-700 flex items-center gap-2">
              <Mail size={13} />
              Email delivery requires the <code className="font-mono bg-blue-100 px-1 rounded">send-announcement</code> Edge Function deployed in Supabase.
            </p>
            <p className="text-xs text-blue-600 mt-1">Announcements save to the database immediately regardless.</p>
          </div>
        )}
      </Modal>
    </div>
  )
}